<template>
  <div class="flex flex-row h-screen bg-gray-100">
    <!-- Left Sidebar - Group List -->
    <div class="w-1/3 max-w-sm bg-white shadow-lg overflow-hidden flex flex-col">
      <!-- Header -->
      <div class="bg-gradient-to-r from-blue-600 to-indigo-800 px-6 py-4">
        <h1 class="text-3xl font-bold text-white">Chats</h1>
        <!-- Search Bar -->
        <div class="mt-4 relative">
          <input 
            type="text" 
            class="w-full pl-10 pr-4 py-2 rounded-lg bg-white bg-opacity-20 text-white placeholder-gray-200 focus:outline-none focus:ring-2 focus:ring-white"
            placeholder="Search Groups" 
            v-model="groupSearchData.keyword" 
            @keyup="getChatGroups"
          >
          <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd"></path>
            </svg>
          </div>
        </div>
        
        <!-- Filter Buttons -->
        <div class="flex mt-4 space-x-2">
          <router-link :to="'/' + currentUrl + '/one-to-one-chat'">
            <button class="px-6 py-2 rounded-lg bg-indigo-900 bg-opacity-40 text-white font-medium hover:bg-opacity-60 transition duration-200">
              Inbox
            </button>
          </router-link>
          <router-link :to="'/' + currentUrl + '/group-chat'">
            <button class="px-6 py-2 rounded-lg bg-white text-indigo-800 font-medium shadow-md hover:bg-gray-100 transition duration-200">
              Groups
            </button>
          </router-link>
          
          <!-- Add Group Button -->
          <a 
            href="javascript:void(0)" 
            v-b-modal.create-group-modal 
            v-if="user.role == 'COMPANY' && company.role=='COMPANY_ADMIN'"
            class="flex items-center justify-center w-10 h-10 rounded-full bg-indigo-900 bg-opacity-40 hover:bg-opacity-60 transition duration-200 ml-1"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
          </a>
        </div>
      </div>
      
      <!-- Group List -->
      <div class="flex-1 overflow-y-auto px-4 py-2">
        <ul class="space-y-1">
          <li 
            v-for="e in chatGroups.data" 
            v-bind:key="e.id"
            class="border-b border-gray-100 last:border-b-0"
          >
            <router-link 
              :to="'/' + currentUrl + '/group-chat/' + e.id" 
              @click.native="readGroupMessage(e.id)"
              class="block p-3 rounded-lg hover:bg-gray-50 transition duration-200"
            >
              <div class="flex items-center space-x-3">
                <div class="relative flex-shrink-0">
                  <!-- Group Avatar -->
                  <div class="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center border-2 border-gray-200">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                </div>
                
                <div class="flex-1 min-w-0">
                  <div class="flex justify-between items-center">
                    <h3 class="text-sm font-semibold text-gray-900 truncate">
                      {{ e.name }}
                    </h3>
                    <span class="text-xs text-gray-500">{{ getLastMessageDate(e) | fromNow }}</span>
                  </div>
                  <p class="text-sm text-gray-500 truncate">{{ getLastMessage(e) }}</p>
                </div>
                
                <div v-if="e.new_message.length" class="flex-shrink-0 ml-2">
                  <span class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-indigo-600 text-xs font-medium text-white">
                    {{e.new_message.length}}
                  </span>
                </div>
              </div>
            </router-link>
          </li>
        </ul>
      </div>
    </div>
    
    <!-- Right Side - Chat Area -->
    <div class="flex-1 flex flex-col bg-white shadow-lg rounded-l-xl overflow-hidden">
      <div v-if="this.$route.params.id">
        <!-- Chat Header -->
        <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div class="flex items-center">
            <!-- Group Avatar -->
            <div class="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center border-2 border-gray-200">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div class="ml-4">
              <h2 class="text-xl font-semibold text-gray-800">{{ groupChat.name }}</h2>
              <p class="text-sm text-gray-500">
                <span v-if="groupChat.members">{{ groupChat.members.length + 1 }} members</span>
              </p>
            </div>
          </div>
          
          <div class="flex items-center space-x-3">
            <!-- View Members Button -->
            <button 
              class="px-4 py-2 bg-indigo-50 text-indigo-700 rounded-lg text-sm font-medium hover:bg-indigo-100 transition duration-200 focus:outline-none"
              v-b-modal.group-members-modal
            >
              View Members
            </button>
            
            <!-- Options Button -->
            <button class="p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
              </svg>
            </button>
          </div>
        </div>
        
        <!-- Messages Area -->
        <div id="chat-gui" class="flex-1 overflow-y-auto p-6 bg-gray-50" v-chat-scroll>
          <div class="flex justify-center mb-4" v-if="groupData.limit < total">
            <button 
              class="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg font-medium hover:bg-indigo-200 transition duration-200 focus:outline-none" 
              @click="loadMoreMessages"
            >
              Load More Messages
            </button>
          </div>
          
          <div class="space-y-4">
            <div v-for="m in messagesList" v-bind:key="m.id">
              <!-- Received Message -->
              <div class="flex items-end space-x-2" v-if="authUser.emp_id != m.sender_id">
                <img 
                  class="w-8 h-8 rounded-full flex-shrink-0" 
                  :src="imagePath + '/profile-images/' + m.profile_image" 
                  alt="Profile"
                >
                
                <div class="max-w-md">
                  <div v-if="m.message_type == 'TEXT'" class="bg-white rounded-t-lg rounded-r-lg p-4 shadow-sm">
                    <div class="text-gray-700 text-sm" v-html="convertToHtml(m.content)"></div>
                  </div>
                  
                  <div v-if="m.message_type == 'FILE'" class="bg-white rounded-t-lg rounded-r-lg p-4 shadow-sm">
                    <a href="javascript:void(0)" @click="downloadAttachment(m.id, m.content, 'groupChat')" class="flex items-center space-x-2 text-indigo-600 hover:text-indigo-800">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span class="font-medium">Download Attachment</span>
                    </a>
                  </div>
                  
                  <div class="mt-1 text-xs text-gray-500">
                    <span class="font-medium">{{ m.first_name }} {{ m.last_name }}</span> • {{ m.created_at | fromNow }}
                  </div>
                </div>
              </div>
              
              <!-- Sent Message -->
              <div class="flex items-end justify-end space-x-2" v-else>
                <div class="max-w-md">
                  <div v-if="m.message_type == 'TEXT'" class="bg-indigo-600 rounded-t-lg rounded-l-lg p-4 shadow-sm">
                    <div class="text-white text-sm" v-html="convertToHtml(m.content)"></div>
                  </div>
                  
                  <div v-if="m.message_type == 'FILE'" class="bg-indigo-600 rounded-t-lg rounded-l-lg p-4 shadow-sm">
                    <a href="javascript:void(0)" @click="downloadAttachment(m.id, m.content, 'groupChat')" class="flex items-center space-x-2 text-white hover:text-indigo-100">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span class="font-medium">Download Attachment</span>
                    </a>
                  </div>
                  
                  <div class="mt-1 text-xs text-gray-500 text-right">{{ m.created_at | fromNow }}</div>
                </div>
                
                <img 
                  class="w-8 h-8 rounded-full flex-shrink-0" 
                  :src="imagePath + '/profile-images/' + m.profile_image" 
                  alt="Profile"
                >
              </div>
            </div>
          </div>
        </div>
        
        <!-- Message Input -->
        <div class="p-4 border-t border-gray-200 bg-white">
          <div class="flex items-end space-x-2">
            <div class="flex-1 relative">
              <textarea 
                type="text" 
                placeholder="Type your message..." 
                class="w-full px-4 py-3 rounded-lg bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                rows="1"
                v-model="dataMsg.message" 
                v-on:keyup.enter="sendGroupChatMessage"
              ></textarea>
              
              <button 
                type="button" 
                class="absolute bottom-3 right-3 text-gray-500 hover:text-indigo-600 focus:outline-none" 
                @click="$refs.file.click()"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
              </button>
              <input type="file" ref="file" class="hidden" @change="sendGroupAttachment">
            </div>
            
            <button 
              type="button" 
              class="flex items-center justify-center p-3 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-md"
              @click="sendGroupChatMessage"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 transform rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      
      <!-- Empty State -->
      <div v-else class="flex-1 flex flex-col items-center justify-center bg-gray-50">
        <div class="p-8 text-center">
          <div class="inline-flex justify-center items-center w-24 h-24 rounded-full bg-indigo-100 mb-6">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <h3 class="text-2xl font-bold text-gray-800 mb-2">No group selected</h3>
          <p class="text-gray-500 max-w-md">Select a group from the list or create a new group to start chatting with your team.</p>
        </div>
      </div>
    </div>
    
    <!-- Create Group Modal -->
    <b-modal id="create-group-modal" title="Create New Group" :hide-footer="hideFooter" class="create-group-modal">
      <div class="p-4">
        <h3 class="text-lg font-medium text-gray-900 mb-4">Create New Group</h3>
        
        <!-- Group Name Input -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Group Name</label>
          <input 
            type="text" 
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Enter Group Name" 
            v-model="createGroup.name"
          />
        </div>
        
        <!-- Employee Search -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Add Employees</label>
          <div class="relative">
            <input 
              type="text" 
              class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Search employees to add" 
              v-model="searchData.name"
              @keyup="getEmployeesListChat"
            />
            <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <svg class="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd"></path>
              </svg>
            </div>
          </div>
        </div>
        
        <!-- Employee List -->
        <div class="mb-6 max-h-60 overflow-y-auto border border-gray-200 rounded-md p-2">
          <div 
            v-for="e in empList.data" 
            v-bind:key="e.id"
            class="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded-md"
          >
            <input 
              type="checkbox" 
              v-model="createGroup.user" 
              :id="'employee-' + e.id" 
              :value="e.id"
              class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label :for="'employee-' + e.id" class="flex items-center space-x-3 cursor-pointer flex-1">
              <img 
                class="w-8 h-8 rounded-full object-cover" 
                :src="imagePath + '/profile-images/' + e.profile_image || 'https://images.unsplash.com/photo-1590031905470-a1a1feacbb0b?ixlib=rb-1.2.1&amp;auto=format&amp;fit=facearea&amp;facepad=3&amp;w=144&amp;h=144'" 
                alt="Profile" 
              />
              <span class="text-sm font-medium text-gray-900">{{ e.first_name }} {{ e.last_name }}</span>
            </label>
          </div>
        </div>
        
        <!-- Create Button -->
        <button 
          type="button" 
          class="w-full px-4 py-2 bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          @click="createChatGroup" 
          :disabled="createGroup.disabled"
        >
          Create Group
        </button>
      </div>
    </b-modal>
    
    <!-- Group Members Modal -->
    <b-modal id="group-members-modal" :title="'Members in ' + groupChat.name" :hide-footer="hideFooter" class="group-members-modal">
      <div class="p-4">
        <h3 class="text-lg font-medium text-gray-900 mb-4">Group Members</h3>
        
        <!-- Admin -->
        <div class="flex items-center space-x-3 p-3 bg-indigo-50 rounded-lg mb-2">
          <div class="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <div>
            <div class="text-sm font-medium text-gray-900">{{groupChat.first_name}} {{groupChat.last_name}}</div>
            <div class="text-xs text-indigo-600 font-medium">Group Admin</div>
          </div>
        </div>
        
        <!-- Members -->
        <div class="space-y-2 max-h-80 overflow-y-auto">
          <div 
            v-if="groupChat.members" 
            v-for="m in groupChat.members" 
            v-bind:key="m.id"
            class="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg"
          >
            <img 
              class="w-10 h-10 rounded-full object-cover" 
              :src="imagePath + '/profile-images/' + m.profile_image || 'https://images.unsplash.com/photo-1590031905470-a1a1feacbb0b?ixlib=rb-1.2.1&amp;auto=format&amp;fit=facearea&amp;facepad=3&amp;w=144&amp;h=144'" 
              alt="Profile" 
            />
            <div class="text-sm font-medium text-gray-900">{{m.first_name}} {{m.last_name}}</div>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
  </template>
  
  <script>
  /* eslint-disable */
  import AppMixin from '../../../mixins/AppMixin'
  import Api from '../../../router/api'
  
  export default {
    name: 'GroupChat',
    mixins: [AppMixin],
    data() {
      return {
        createGroup: {
          user: [],
          name: '',
          disabled: false
        },
        chatGroups: [],
        hideFooter: true,
        dataMsg: {
          message: '',
          rId: ''
        },
        imageMsg: {
          file: '',
          type: 'groupChat',
          rId: ''
        },
        currentUrl: '',
        chatGroups: [],
      }
    },
    methods: {
      createChatGroup: function () {
        let that = this;
        if (!that.createGroup.name) {
          this.$swal({
            icon: "error",
            title: "Error",
            text: "Please enter group name",
            showConfirmButton: true
          })
        } else if (!that.createGroup.user.length) {
          this.$swal({
            icon: "error",
            title: "Error",
            text: "Please select at least one group member",
            showConfirmButton: true
          })
        } else {
          Api.createChatGroup(that.createGroup).then(response => {
            that.createGroup.disabled = false
            this.$swal({
              icon: "success",
              title: "Success",
              text: "Group created successfully",
              showConfirmButton: true
            }).then(function () {
              that.createGroup.disabled = false
              that.createGroup.user = []
              that.createGroup.name = ''
              that.$bvModal.hide('create-group-modal')
              that.getChatGroups();
            });
          }).catch((error) => {
            this.$swal({
              icon: "error",
              title: "Error",
              text: error.response.data.message,
              showConfirmButton: true
            }).then(function () {
              that.createGroup.disabled = false
            });
          });
        }
      },
      getLastMessage(e){
        if(e.last_messages && e.last_messages.messages &&  e.last_messages.messages.content){
          return e.last_messages.messages.content
        }
        return '';
      },
      getLastMessageDate(e){
        if(e.last_messages && e.last_messages.messages &&  e.last_messages.messages.created_at){
          return e.last_messages.messages.created_at
        }
        return null;
      },
      loadMoreMessages: function () {
        let that = this
        that.groupData.limit = that.groupData.limit + that.groupData.limit
        that.getGroupChatMessage()
      },
      sendGroupChatMessage: function () {
        let that = this
        if (!that.dataMsg.message) {
          this.$swal({
            icon: 'error',
            title: 'Error',
            text: 'Please enter a message',
            showConfirmButton: true
          })
        } else {
          that.dataMsg.rId = this.$route.params.id
          Api.sendGroupChatMessage(that.dataMsg).then(response => {
            that.dataMsg.message = ''
            that.getGroupChatMessage();
            that.getChatGroups();
          }).catch((error) => {
            this.$swal({
              icon: 'error',
              title: 'Error',
              text: error.response.data.message,
              showConfirmButton: true
            }).then(function () {
              that.anouncements.disabled = false
            })
          })
        }
      },
      sendGroupAttachment: function (e) {
        let that = this
        that.imageMsg.rId = this.$route.params.id
        that.imageMsg.file = that.$refs.file.files[0]
        e.preventDefault()
        const formData = new FormData()
        formData.append('file', that.imageMsg.file)
        formData.append('type', that.imageMsg.type)
        formData.append('rId', that.imageMsg.rId)
        let headers = {
          'Content-Type': 'multipart/form-data',
          'Access-Control-Allow-Origin': '*'
        }
        Api.sendAttachment(formData, headers, 'groupChat').then(response => {
          that.getGroupChatMessage()
        }).catch((error) => {
          this.$swal({
            icon: 'error',
            title: 'Error',
            text: error.response.data.message,
            showConfirmButton: true
          })
        })
      },
      scrollToEnd() {
        $(document).ready(function () {
          $('.chat-gui').animate({
            scrollTop: $('.chat-gui .chat-list:last-child').position().top
          }, 'slow')
        })
      }
    },
    created() {
      var url = document.URL.split('/');
      this.currentUrl = url[3]
  
      this.getChatGroups();
      this.getEmployeesListChat()
      this.getAuthUser()
      this.getChatGroup(this.$route.params.id)
      this.getGroupChatMessage()
      if (this.$route.name == 'GroupChat') {
        window.Echo.channel('group' + this.user.id)
          .listen('GroupMessageSent', (e) => {
            this.messagesList.push(
              {
                'id': e.groupMessageSent.id,
                'content': e.groupMessageSent.content,
                'message_type': e.groupMessageSent.message_type,
                'first_name': e.first_name,
                'last_name': e.last_name,
                'profile_image': e.profile_image,
                'created_at': e.groupMessageSent.created_at
              })
            // this.getGroupChatMessage()
          });
      }
    }
  }

</script>


// WEBPACK FOOTER //
// src/components/common/chat/GroupChat.vue